/**
 * @format
 */

import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus';
import { DocumentService } from '@/scripts/services/document/document-service';
import { sortBy, startCase } from 'lodash';

export const DocumentStatus = Object.freeze({
    DRAFT: 'Draft',
    SENT: 'Sent',
    VIEWED: 'Viewed',
    SIGNED: 'Signed',
    COMPLETED: 'Completed',
    EXPIRED: 'Expired',
    DECLINED: 'Declined',
    PENDING_APPROVAL: 'Pending Approval',
});

export default {
    data() {
        return {
            backupJson: '',
            signersPerPageInCertificate: 7,
        };
    },

    computed: {
        routeBaseUrl() {
            return this.$store.getters.routeBaseUrl;
        },
    },
    methods: {
        mapDocumentCategories(items) {
            return items.map((item) => ({
                id: item.Id,
                name: item.Category,
            }));
        },

        mapCategories(categories) {
            if (categories) {
                return categories.map(({ Category }) => Category);
            } else {
                return [];
            }
        },

        mapRecipients(recipients, hasSigningOrder = false) {
            if (recipients) {
                const mappedRecipients = recipients.map((recipient) => {
                    const recipientType = this.getRecipientType(recipient);

                    return {
                        id: recipient.Id,
                        userId: recipient.UserId,
                        contactId: recipient.ContactId,
                        name: recipient.Name,
                        title: recipient[recipientType] ? recipient[recipientType].Title : '',
                        company: recipientType === 'Contact' ? recipient.Contact.Company : '',
                        email: recipient.Email,
                        profilePicture: recipient[recipientType]
                            ? recipient[recipientType].ProfileImageUrl
                            : null,
                        color: recipient.Color,
                        signingOrder: recipient.SigningOrder || 0,
                        type: recipientType,
                        hasCompletedDocument: recipient.HasCompletedDocument,
                        isAbleToSign: recipient.IsAbleToSign,
                        isSigner: recipient.IsSigner,
                        isDelegatedSigner: recipient.IsDelegatedSigner,
                        ipAddress: recipient.IPAddress,
                        location: recipient.Location,
                        signedDate: recipient.SignedDate
                            ? this.getLocalMoment(recipient.SignedDate)
                            : null,
                        viewDate: recipient.ViewedDate
                            ? this.getLocalMoment(recipient.ViewedDate)
                            : null,
                        sentDate: recipient.SentDate
                            ? this.getLocalMoment(recipient.SentDate)
                            : null,
                        signature: recipient.Signature,
                    };
                });

                return hasSigningOrder
                    ? sortBy(mappedRecipients, ['signingOrder'])
                    : sortBy(mappedRecipients, ['id']);
            } else {
                return [];
            }
        },

        getRecipientType(recipient) {
            if (recipient.UserId) {
                return 'User';
            } else if (recipient.ContactId) {
                return 'Contact';
            } else {
                return 'Custom';
            }
        },

        getLocalMoment(date, addUTC = true) {
            return `${this.$moment.utc(date).format('MMM D, YYYY')} | ${this.$moment
                .utc(date)
                .format('h:mm A')} ${addUTC ? 'UTC' : ''}`;
        },

        setupDocumentInformation(
            { data },
            categoriesEditionMode = false,
            includeCertificate = false,
        ) {
            let item = null;
            if (data.value) {
                [item] = data.value;
            } else {
                item = { ...data };
            }
            this.backupJson = item.TemplateJson;

            let mappedCategories = [];
            if (item.Categories) {
                mappedCategories = categoriesEditionMode
                    ? this.mapCategories(item.Categories)
                    : this.mapDocumentCategories(item.Categories);
            }
            const mappedRecipients = this.mapRecipients(item.Recipients, item.HasSigningOrder);

            const createdBy = item.CreatedByUser
                ? {
                      name: `${item.CreatedByUser.FirstName} ${item.CreatedByUser.LastName}`,
                      email: item.CreatedByUser.Email,
                      profileImageUrl: item.CreatedByUser.ProfileImageUrl,
                  }
                : null;

            this.form = {
                ...this.form,
                name: item.Name,
                dealId: item.DealId,
                dealStatusId: item.DealStatusId,
                status: startCase(item.Status),
                categories: mappedCategories,
                hasSigningOrder: item.HasSigningOrder || false,
                recipients: mappedRecipients,
                createdDate: this.$moment(item.CreatedAt),
                createdBy,
                config: item.TemplateJson,
                emailSenderUserId: item.EmailSenderUserId,
                publishedDate: this.$moment(item.PublishedAt),
                updatedDate: this.$moment(item.UpdatedAt),
                modifiedBy: item.UpdatedByUser
                    ? {
                          name: `${item.UpdatedByUser.FirstName} ${item.UpdatedByUser.LastName}`,
                          profileImageUrl: item.UpdatedByUser.ProfileImageUrl,
                      }
                    : null,
                settings: {
                    warnOneDayPrior: item.WarnOneDayPrior,
                    recurringReminderDays: item.RecurringReminderDays,
                    firstReminderDays: item.FirstReminderDays,
                    expirationDays: item.ExpirationDays,
                },
            };

            if (includeCertificate && item.Status === DocumentStatus.COMPLETED) {
                this.certificateInfo = {
                    document: {
                        id: item.Guid,
                        createdDate: this.getLocalMoment(item.CreatedAt, false),
                        completedDate: this.getLocalMoment(item.CompletedDate, false),
                        ownerName: item.CreatedByUser
                            ? `${item.CreatedByUser.FirstName} ${item.CreatedByUser.LastName}`
                            : '',
                    },

                    chuncksOfSigners: this.certificatePages(this.form.recipients),
                };
            }
        },

        getSignersFromRecipients(recipients) {
            const signers = [];

            recipients.forEach((recipient) => {
                if (recipient.isSigner || recipient.isDelegatedSigner) {
                    signers.push(recipient);
                }
            });

            return signers;
        },

        certificatePages(recipients) {
            const cards = this.getSignersFromRecipients(recipients);

            const maxPageHeight = 750;
            let currentPage = [];
            let currentPageHeight = 0;
            const pages = [currentPage];

            for (const card of cards) {
                const cardHeight = 103.25 + this.getExtraCardHeight(card);

                if (currentPageHeight + cardHeight > maxPageHeight) {
                    currentPage = [card];
                    currentPageHeight = cardHeight;
                    pages.push(currentPage);
                } else {
                    currentPage.push(card);
                    currentPageHeight += cardHeight;
                }
            }

            return pages;
        },

        getExtraCardHeight(card) {
            const extraCardHeight =
                this.calculateLineBreaks(card.name, 'name') * 14.96 +
                this.calculateLineBreaks('(' + card.email + ')', 'email') * 13.09 +
                this.calculateLineBreaks('IP Address: ' + card.ipAddress, 'ipAddress') * 13.09 +
                this.calculateLineBreaks('Location: ' + card.location, 'location') * 13.09;
            return extraCardHeight;
        },

        calculateLineBreaks(word) {
            switch (word) {
                case 'name':
                    if (word.length > 25) {
                        return Math.ceil(word.length / 25) - 1;
                    } else {
                        return 0;
                    }

                default:
                    if (word.length > 36) {
                        return Math.ceil(word.length / 36) - 1;
                    } else {
                        return 0;
                    }
            }
        },

        setupDocumentTemplateInformation({ data }, categoriesEditionMode) {
            let item = null;
            if (data.value) {
                [item] = data.value;
            } else {
                item = { ...data };
            }
            let mappedCategories = [];
            if (item.Categories) {
                mappedCategories = categoriesEditionMode
                    ? this.mapCategories(item.Categories)
                    : this.mapDocumentCategories(item.Categories);
            }

            const createdBy = item.CreatedByUser
                ? {
                      name: `${item.CreatedByUser.FirstName} ${item.CreatedByUser.LastName}`,
                      email: item.CreatedByUser.Email,
                      profileImageUrl: item.CreatedByUser.ProfileImageUrl,
                  }
                : null;

            this.form = {
                ...this.form,
                id: item.Id,
                name: item.Name,
                categories: mappedCategories,
                createdDate: this.$moment(item.CreatedAt),
                createdBy,
                config: item.TemplateJson,
                publishedDate: this.$moment(item.PublishedAt),
                updatedDate: this.$moment(item.UpdatedAt),
            };
        },

        openEditRecipientPopup(config, documentId) {
            this.actions = {
                confirm: {
                    label: 'Void',
                    type: 'removeNotIcon',
                    action: () => this.initVoidEditDocument(config, documentId),
                },

                tertiary: {
                    label: 'Cancel',
                    action: () => null,
                },
            };
            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Edit Recipient',
                    actions: this.actions,
                    element: {
                        name: 'void-document-popup',
                        type: 'edit-recipient',
                    },
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        openVoidPopup(config, documentId) {
            this.actions = {
                confirm: {
                    label: 'Void',
                    type: 'removeNotIcon',
                    action: () => this.initVoidDocument(config, documentId),
                },

                tertiary: {
                    label: 'Cancel',
                    action: () => null,
                },
            };
            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Void Document',
                    actions: this.actions,
                    content:
                        '<p>Are you sure you want to void this document? All recipients tied to this document will be unable to view or interact with it until you send it again.</p><br/><p> A voided document is sent back to the status of draft where you can make any modifications needed.</p>',
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        openRemoveRecipientPopup(config, documentId) {
            this.actions = {
                confirm: {
                    label: 'Void',
                    type: 'removeNotIcon',
                    action: () => this.initVoidEditDocument(config, documentId),
                },

                tertiary: {
                    label: 'Cancel',
                    action: () => null,
                },
            };
            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Remove Recipient',
                    actions: this.actions,
                    element: {
                        name: 'void-document-popup',
                        type: 'edit-recipient',
                    },
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        openEditDocumentPopup(config, documentId) {
            this.actions = {
                confirm: {
                    label: 'Void',
                    type: 'removeNotIcon',
                    action: () => this.initVoidEditDocument(config, documentId),
                },

                tertiary: {
                    label: 'Cancel',
                    action: () => null,
                },
            };
            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Edit Document',
                    actions: this.actions,
                    element: {
                        name: 'void-document-popup',
                        type: 'edit-document',
                    },
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        initVoidEditDocument(config, documentId) {
            this.cleanInputs(config);
            this.voidDocument(documentId);
        },

        initVoidDocument(config, documentId) {
            this.cleanInputs(config);
            this.voidDocument(documentId, false);
        },

        cleanInputs(config) {
            this.voidConfiguration = this.findElementsInConfiguration(config);
        },

        findElementsInConfiguration(config) {
            Object.keys(config).forEach((key) => {
                if (key === 'elements') {
                    config[key].forEach((element) => {
                        this.emptyInputs(element);
                    });
                }
                if (typeof config[key] === 'object' && config[key] !== null) {
                    this.findElementsInConfiguration(config[key]);
                }
            });

            return config;
        },

        emptyInputs(element) {
            switch (element.type) {
                case 'text-field':
                case 'date-stamp':
                    this.emptyTextField(element);
                    break;
                case 'signature':
                    this.emptySignature(element);
                    break;
                case 'checkbox':
                    this.emptyCheckbox(element);
                    break;
                case 'radio-button':
                    this.emptyRadioButton(element);
                    break;
            }
        },

        emptyTextField(element) {
            element.content.body = '';
        },

        emptySignature(element) {
            element.content.link = null;
        },

        emptyCheckbox(element) {
            element.content.isChecked = false;
        },

        emptyRadioButton(element) {
            element.content.selectedOption = null;
            element.content.recipientSelected = false;
        },

        voidDocument(documentId, redirectToEdit = true) {
            const configuration = {
                DocumentId: documentId,
                TemplateJson: JSON.stringify(this.voidConfiguration),
            };
            DocumentService.voidDocument(configuration)
                .then(() => {
                    this.$helpers.showSuccessNotification('This document has been voided');
                    EventBus.$emit('documents:update');
                    redirectToEdit &&
                        this.$router.push(`${this.routeBaseUrl}/Documents/${documentId}/Design`);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred by voiding your document.',
                        { error },
                    );
                });
        },
    },
};
